export function isOwnerOrAdmin(user) {
    const roleName = (user?.role_name || "").toLowerCase();
    return roleName === "owner" || roleName === "admin" || !!user?.role_perms?.all;
}

export function isDashboardUser(user) {
    if (!user) return false;
    if (isOwnerOrAdmin(user)) return true;
    const roleName = (user?.role_name || "").toLowerCase();
    if (["farm manager", "manager", "accounting", "viewer"].includes(roleName)) return true;
    const perms = user?.role_perms || {};
    return !!(perms.manage_finance || perms.manage_inventory || perms.manage_reports || perms.manage_settings || perms.manage_users || perms.approve);
}

export function normalizedRole(user) {
    const role = (user?.role_name || "").trim().toLowerCase().replaceAll("_", " ").replaceAll("-", " ");
    if (["admin"].includes(role)) return "owner";
    if (["employee", "staff"].includes(role)) return "worker";
    if (role === "vet") return "veterinarian";
    if (role === "customer") return "buyer";
    return role;
}

export function canEnterFieldMode(user) {
    return ["owner", "farm manager", "manager", "worker", "veterinarian"].includes(normalizedRole(user));
}

export function getFieldModeRouteForUser(user) {
    if (!canEnterFieldMode(user)) return "";
    return isDashboardUser(user) ? "/field-mode" : "/field";
}

const VETERINARIAN_ROUTES = [
    "/livestock", "/species", "/animals/", "/growth", "/birth-to-sale",
    "/alerts", "/notifications", "/reports", "/forms", "/incidents", "/scan", "/calendar", "/field-mode",
];

const OPERATIONAL_CALENDAR_ROLES = new Set(["owner", "farm manager", "manager", "veterinarian"]);

export function canAccessPersonalTasks(user) {
    return ["owner", "farm manager", "manager", "worker"].includes(normalizedRole(user));
}

export function canAccessDashboardPath(user, pathname) {
    const role = normalizedRole(user);
    const path = pathname || "/";
    if (path === "/calendar" || path.startsWith("/calendar/")) {
        return OPERATIONAL_CALENDAR_ROLES.has(role);
    }
    if (path === "/worker" || path.startsWith("/worker/")) {
        return canAccessPersonalTasks(user);
    }
    if (role === "veterinarian") {
        return VETERINARIAN_ROUTES.some((allowed) => path === allowed || path.startsWith(allowed));
    }
    if (["worker", "buyer"].includes(role)) return false;
    return true;
}

export function getHomeRouteForUser(user) {
    if (normalizedRole(user) === "veterinarian") return "/livestock";
    return isDashboardUser(user) ? "/dashboard" : "/field";
}

export function normalizeNextPath(nextRaw) {
    if (typeof nextRaw !== "string") return "";
    const next = nextRaw.trim();
    // Only allow same-origin absolute paths.
    if (!next.startsWith("/")) return "";
    if (next.startsWith("//")) return "";
    return next;
}

export function resolveLoginRedirect(user, nextRaw) {
    const fallback = getHomeRouteForUser(user);
    const next = normalizeNextPath(nextRaw);
    if (!next || next === "/" || next.startsWith("/login")) return fallback;

    // Never replay an Owner-only destination for a non-Owner after login. The
    // route guard independently enforces the same boundary for live navigation.
    if ((next === "/admin/recycle-bin" || next.startsWith("/admin/recycle-bin/")) && normalizedRole(user) !== "owner") {
        return fallback;
    }

    const dashboardUser = isDashboardUser(user);
    const fieldRoute = next === "/field" || next.startsWith("/field/");

    if (dashboardUser) {
        // Dashboard-capable Field Mode users use the owner-shell route while
        // keeping the employee shell isolated from management navigation.
        if (fieldRoute) return getFieldModeRouteForUser(user) || fallback;
        return next;
    }

    // Employee users should never be sent to dashboard shell.
    if (next === "/dashboard" || next.startsWith("/dashboard/")) return fallback;
    return next;
}
